import React from 'react';

import { isDefined } from '@nfctron/nexus-core';
import { useContextRequired } from '@nfctron/nexus-react';
import { BagIcon } from '@nfctron/nexus-react-svg';

import { ActionIcon, Stepper } from '@mantine/core';
import { CheckIcon, ChevronLeftIcon, ClockIcon, SendIcon, ShoppingCartIcon, TicketIcon } from 'lucide-react';

import { getEventTypeSensitiveComponentDisplayName } from '@root/pages/event/[organizer]/[event]/_resources/helpers/common';

import { CountdownAlert } from '@root/lib/components/CountdownAlert/CountdownAlert';
import { MultiView } from '@root/lib/components/MultiView/MultiView';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { ReservationExpiredModal } from '@root/lib/modals/components/Reservation/ReservationExpired.modal';
import { openModal } from '@root/lib/modals/utils/openModal';
import { type AtomComponent } from '@root/lib/types/components';

import { EventCartPageContext } from '../components/CartContext';
import { CART_VIEW, cartStoreSelectors, useCartStore } from '../stores/cartStore';

/**
 * Event/Cart/StepperControl atom component for displaying order progress
 * Shows the status of different stages in the order process
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const StepperControlAtom: AtomComponent = () => {
	const { event } = useContextRequired(EventCartPageContext);
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, StepperControlAtom.displayName);
	const { t: commonT } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const { t: typeSensitiveT } = useTranslate(
		APP_I18N_NAMESPACE.MAIN,
		getEventTypeSensitiveComponentDisplayName(StepperControlAtom.displayName, event.eventType.eventTypeId),
	);
	const getCartViewPos = useCartStore((s) => s.getCartViewPos);
	const ticketsViewIs = useCartStore((s) => s.getCartViewEvaluated({ view: CART_VIEW.TICKETS }));
	const cartViewIs = useCartStore((s) => s.getCartViewEvaluated({ view: CART_VIEW.CART }));
	const productsViewIs = useCartStore((s) => s.getCartViewEvaluated({ view: CART_VIEW.PRODUCTS }));
	const checkoutViewIs = useCartStore((s) => s.getCartViewEvaluated({ view: CART_VIEW.CHECKOUT }));
	const previousEnabledStep = useCartStore((s) => s.getPrevCartView({ prop: 'isEnabled' }));
	const setCartView = useCartStore((s) => s.setCartView);
	const currentCartViewPos = useCartStore((s) => s.getCartViewPos({ cartView: s.currentCartView, humanReadable: false, onlyVisible: false }));
	const getCartViewAtPos = useCartStore((s) => s.getCartViewAtPos);
	const resetCartedTicketsForm = useCartStore((s) => s.resetCartedTicketsForm);
	const cartState = useCartStore((s) => cartStoreSelectors.getCartState(s));
	const seatingReservation = useCartStore((s) => s.seatingReservation);
	const clearSeatingReservation = useCartStore((s) => s.clearSeatingReservation);

	/** get tickets view heading based on available options */
	const getTicketsViewHeading = React.useCallback(() => {
		/** ticket step normal */
		if (cartState.isTicketSaleEnabled) {
			return (
				<h2 className="text-lucky-point-500 w-full grow truncate text-xl font-normal xl:text-2xl">
					{typeSensitiveT('ticketStep.title', {
						position: getCartViewPos({ cartView: CART_VIEW.TICKETS, humanReadable: true, onlyVisible: true })[0],
					})}
				</h2>
			);
		}

		/** ticket step with import enabled */
		if (cartState.isTicketImportEnabled) {
			return (
				<h2 className="text-lucky-point-500 w-full grow truncate text-xl font-normal xl:text-2xl">
					{t('ticketImportStep.title', {
						position: getCartViewPos({ cartView: CART_VIEW.TICKETS, humanReadable: true, onlyVisible: true })[0],
					})}
				</h2>
			);
		}

		/** ticket step with physical ticket top up enabled */
		if (cartState.isPhysicalTicketTopUpEnabled) {
			return (
				<h2 className="text-lucky-point-500 w-full grow truncate text-xl font-normal xl:text-2xl">
					{typeSensitiveT('ticketPhysicalTopUpStep.title', {
						position: getCartViewPos({ cartView: CART_VIEW.TICKETS, humanReadable: true, onlyVisible: true })[0],
					})}
				</h2>
			);
		}

		return null;
	}, [getCartViewPos, cartState.isPhysicalTicketTopUpEnabled, cartState.isTicketImportEnabled, cartState.isTicketSaleEnabled, t, typeSensitiveT]);

	/** get descriptive text for the tickets view based on configuration */
	const ticketsViewText = React.useMemo(() => {
		/** ticket step normal */
		if (cartState.isTicketSaleEnabled) {
			return (
				<p className="text-base text-zinc-700 xl:text-lg" aria-describedby="tickets-step">
					{typeSensitiveT('ticketStep.text')}
				</p>
			);
		}

		/** ticket step with import enabled */
		if (cartState.isTicketImportEnabled) {
			return (
				<p className="text-base text-zinc-700 xl:text-lg" aria-describedby="tickets-step">
					{t('ticketImportStep.text')}
				</p>
			);
		}

		/** ticket step with physical ticket top up enabled */
		if (cartState.isPhysicalTicketTopUpEnabled) {
			return (
				<p className="text-base text-zinc-700 xl:text-lg" aria-describedby="tickets-step">
					{t('ticketPhysicalTopUpStep.text')}
				</p>
			);
		}

		return null;
	}, [cartState.isPhysicalTicketTopUpEnabled, cartState.isTicketImportEnabled, cartState.isTicketSaleEnabled, t, typeSensitiveT]);

	return (
		<section
			className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4"
			aria-label={t('stepperControl.aria.section')}
			data-testid="stepper-control"
		>
			<header className="mb-2 flex items-start justify-between gap-3">
				{/* tickets view heading */}
				<MultiView.View view={CART_VIEW.TICKETS}>{getTicketsViewHeading()}</MultiView.View>

				{/* cart view heading */}
				<MultiView.View view={CART_VIEW.CART}>
					<div className="flex items-center gap-2">
						{ticketsViewIs.isVisible && (
							<ActionIcon
								variant="transparent"
								radius="xl"
								onClick={() => {
									/** reset form when switching back to tickets view */
									if (previousEnabledStep === CART_VIEW.TICKETS) {
										resetCartedTicketsForm();
									}
									setCartView(previousEnabledStep);
								}}
								color="lucky-point"
								size="md"
								aria-label={t('stepperControl.aria.backButton')}
							>
								<ChevronLeftIcon size={21} aria-hidden="true" />
							</ActionIcon>
						)}
						<h2 className="text-lucky-point-500 w-full grow truncate text-xl font-normal xl:text-2xl">
							{t('cartStep.title', {
								position: getCartViewPos({ cartView: CART_VIEW.CART, humanReadable: true, onlyVisible: true })[0],
							})}
						</h2>
					</div>
				</MultiView.View>

				{/* products view heading */}
				<MultiView.View view={CART_VIEW.PRODUCTS}>
					<div className="flex items-center gap-2">
						<ActionIcon
							variant="transparent"
							radius="xl"
							onClick={() => setCartView(previousEnabledStep)}
							color="lucky-point"
							size="md"
							aria-label={t('stepperControl.aria.backButton')}
						>
							<ChevronLeftIcon size={21} aria-hidden="true" />
						</ActionIcon>
						<h2 className="text-lucky-point-500 w-full grow truncate text-xl font-normal xl:text-2xl">
							{t('productStep.title', {
								position: getCartViewPos({ cartView: CART_VIEW.PRODUCTS, humanReadable: true, onlyVisible: true })[0],
							})}
						</h2>
					</div>
				</MultiView.View>

				{/* checkout view heading */}
				<MultiView.View view={CART_VIEW.CHECKOUT}>
					<div className="flex items-center gap-2">
						<ActionIcon
							variant="transparent"
							radius="xl"
							onClick={() => setCartView(previousEnabledStep)}
							color="lucky-point"
							size="md"
							aria-label={t('stepperControl.aria.backButton')}
						>
							<ChevronLeftIcon size={21} aria-hidden="true" />
						</ActionIcon>
						<h2 className="text-lucky-point-500 w-full grow truncate text-xl font-normal xl:text-2xl">
							{t('checkoutStep.title', {
								position: getCartViewPos({ cartView: CART_VIEW.CHECKOUT, humanReadable: true, onlyVisible: true })[0],
							})}
						</h2>
					</div>
				</MultiView.View>
			</header>

			<style jsx global>{`
				.mantine-Stepper-step[data-hidden='true'] + .mantine-Stepper-separator {
					display: none !important;
				}
				.mantine-Stepper-step[data-completed='true'] + .mantine-Stepper-separator {
					background-color: #22c55e !important;
				}
			`}</style>

			{/* progress stepper - represents checkout process flow */}
			<nav aria-label={t('stepperControl.aria.navigation')} role="navigation">
				<Stepper
					color="green"
					completedIcon={<CheckIcon className="text-sm" aria-hidden="true" />}
					active={currentCartViewPos[0]}
					onStepClick={(view) => {
						const cartView = getCartViewAtPos(view);
						if (!isDefined(cartView)) return;
						setCartView(cartView);
					}}
					aria-label={t('stepperControl.aria.steps')}
					aria-current="step"
				>
					{/* tickets step */}
					<Stepper.Step
						key="tickets"
						id="tickets-step"
						icon={<TicketIcon className="size-5" aria-hidden="true" />}
						disabled={!ticketsViewIs.isEnabled}
						data-hidden={!ticketsViewIs.isVisible}
						aria-label={t('stepperControl.aria.ticketsStep')}
						{...(!ticketsViewIs.isSelectable && {
							allowStepSelect: false,
							allowStepClick: false,
						})}
					>
						{ticketsViewText}
					</Stepper.Step>

					{/* cart config step */}
					<Stepper.Step
						key="cart"
						id="cart-step"
						icon={<ShoppingCartIcon className="size-5" aria-hidden="true" />}
						disabled={!cartViewIs.isEnabled}
						data-hidden={!cartViewIs.isVisible}
						aria-label={t('stepperControl.aria.cartStep')}
						{...(!cartViewIs.isSelectable && {
							allowStepSelect: false,
							allowStepClick: false,
						})}
					>
						<p className="text-base text-zinc-700 xl:text-lg" aria-describedby="cart-step">
							{t('cartStep.text')}
						</p>
					</Stepper.Step>

					{/* products step */}
					<Stepper.Step
						key="products"
						id="products-step"
						icon={<BagIcon className="size-5" aria-hidden="true" />}
						disabled={!productsViewIs.isEnabled}
						data-hidden={!productsViewIs.isVisible}
						aria-label={t('stepperControl.aria.productsStep')}
						{...(!productsViewIs.isSelectable && {
							allowStepSelect: false,
							allowStepClick: false,
						})}
					>
						<p className="text-base text-zinc-700 xl:text-lg" aria-describedby="products-step">
							{typeSensitiveT('productStep.text')}
						</p>
					</Stepper.Step>

					{/* checkout step */}
					<Stepper.Step
						key="checkout"
						id="checkout-step"
						icon={<SendIcon className="size-5" aria-hidden="true" />}
						disabled={!checkoutViewIs.isEnabled}
						data-hidden={!checkoutViewIs.isVisible}
						aria-label={t('stepperControl.aria.checkoutStep')}
						{...(!checkoutViewIs.isSelectable && {
							allowStepSelect: false,
							allowStepClick: false,
						})}
					>
						<p className="text-base text-zinc-700 xl:text-lg" aria-describedby="checkout-step">
							{t('checkoutStep.text')}
						</p>
					</Stepper.Step>
				</Stepper>
			</nav>

			{/* reservation alert for notifying users about time-limited reservations */}
			{isDefined(seatingReservation) && isDefined(seatingReservation.reservedUntil) && (
				<CountdownAlert
					/** on mobile its shown in the bottom floating footer */
					className="hidden xl:block"
					date={seatingReservation.reservedUntil}
					onExpire={async () => {
						/** handle clear reservation callback before opening the modal */
						await clearSeatingReservation();

						/** open reservation expired modal */
						openModal('reservationExpired', {
							centered: true,
							closeOnClickOutside: false,
							closeOnEscape: false,
							withCloseButton: false,
							title: commonT(`${ReservationExpiredModal.displayName}.title`),
							innerProps: {
								onButtonClick: ({ closeModal, id }) => {
									/** reset tickets dirty state */
									setCartView(CART_VIEW.TICKETS);
									/** close the modal */
									closeModal(id);
								},
							},
						});
					}}
					aria-live="polite"
					role="status"
				>
					{(timeLeft) => (
						<React.Fragment>
							<ClockIcon size={16} aria-hidden="true" />
							{t('reservationAlert.text', { timeLeft })}
						</React.Fragment>
					)}
				</CountdownAlert>
			)}
		</section>
	);
};

StepperControlAtom.displayName = 'Event/Cart/StepperControlAtom';
